@file:OptIn(ExperimentalTime::class)

package com.macrofocus.eflux.ui

import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.network.lightning.Lightning
import com.macrofocus.eflux.network.lightning.LightningCluster
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.risk.RiskComponentPolygon
import com.macrofocus.eflux.network.substation.Substation
import com.macrofocus.eflux.ui.layer.createDashedLineLayer
import deckgl.ControllerProps
import deckgl.DeckGL
import deckgl.FlyToInterpolator
import deckgl.Layer
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import map.ReactMapGL
import mui.material.styles.Theme
import mui.material.styles.useTheme
import org.mkui.react.useProperty
import org.mkui.react.useSingleSelection
import react.*
import web.animations.requestAnimationFrame
import web.timers.*
import kotlin.js.Date
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.ExperimentalTime

public external interface AnimatedDeckGLProps : Props {
    var dataLoader: DataLoader
    var mapStyle: dynamic
    var preLayers: List<Layer>
    var lines: Array<Line>?
    var postLayers: List<Layer>
    var morphing: Double
    var animate: Boolean
    var animateWhileZooming: Boolean
}

public val AnimatedDeckGL = FC<AnimatedDeckGLProps> { props ->
    val theme = useTheme<Theme>()

    val mapRef = useRef<dynamic>(null);
    val zoomingTimer = useRef<Timeout>(null)

    var isZooming by useState(false)
    var currentTime by useState((Date.now() / 100) % 1000)
    var timeoutId = useRef<Interval>()

    val (minZoomLevel, _) = useProperty(props.dataLoader.minZoomLevel)
    val (maxZoomLevel, _) = useProperty(props.dataLoader.maxZoomLevel)

    val (threeDLatitude, setThreeDLatitude) = useProperty(props.dataLoader.threeDLatitude)
    val (threeDLongitude, setThreeDLongitude) = useProperty(props.dataLoader.threeDLongitude)
    val (threeDZoom, setThreeDZoom) = useProperty(props.dataLoader.threeDZoom)
    val (threeDPitch, setThreeDPitch) = useProperty(props.dataLoader.threeDPitch)
    val (threeDBearing, setThreeDBearing) = useProperty(props.dataLoader.threeDBearing)

    val (fps, _) = useProperty(props.dataLoader.fps)
    val (lineWidth, _) = useProperty(props.dataLoader.lineWidth)

    val (probed, _) = useSingleSelection(props.dataLoader.logicalBranchProbing)

    val interpolator = useMemo() {
        FlyToInterpolator(unsafeJso {
            speed = 1.2
            curve = 1.414
        })
    }

    val INITIAL_VIEW_STATE: dynamic = unsafeJso {
        latitude = threeDLatitude // 47.65
        longitude = threeDLongitude // 7
        zoom = threeDZoom // 9.333879434342306 // 6
        pitch = threeDPitch // 60
        bearing = threeDBearing

//        latitude = 46.801111
//        longitude = 8.226667
//        zoom = 7.3
//        pitch = 0 // 30
//        bearing = threeDBearing

        transitionInterpolator = interpolator
        transitionDuration = "auto"

        minZoom = minZoomLevel
        maxZoom = maxZoomLevel
    }

    val viewStateChangeCallback = { state: dynamic ->
        setThreeDLatitude(state.viewState.latitude)
        setThreeDLongitude(state.viewState.longitude)
        setThreeDZoom(state.viewState.zoom)
        setThreeDPitch(state.viewState.pitch)
        setThreeDBearing(state.viewState.bearing)

        if (!props.animateWhileZooming) {
            isZooming = true

            zoomingTimer.current?.let {
                clearTimeout(it)
            }

            // start new one
            zoomingTimer.current = setTimeout(500.milliseconds) {
//                println("Setting isZooming to false")
                isZooming = false
            }
        }

        state.viewState
    }

    val lineLayer =
        createDashedLineLayer(props.dataLoader, props.lines, props.morphing, lineWidth, theme, currentTime, probed)

    fun redraw() {
        if (props.animateWhileZooming || !isZooming) {
            val now = (Date.now() / 100) % 1000
            currentTime = now
            //        println("Redrawing $now")
        }
    }

    fun animate(): Interval {
        redraw()

//        if(props.fps >= 60) {
//            requestAnimationFrame { animate() }
//            return null
//        } else {
        val ms = (1000 / fps).toInt()
        val timeout = setInterval({
            requestAnimationFrame { redraw() }
        }, ms)
        return timeout
//        }
    }

    useEffect(fps, props.animate) {
        timeoutId.current?.let { clearInterval(it) }
        val timeout = if (props.animate) {
            val timeout = animate()
            timeoutId.current = timeout
            timeout
        } else {
            timeoutId.current = null
            null
        }

        awaitCancellation {
            timeout?.let { clearInterval(it) }
        }
    }

    val tooltip = useMemo {
        { info: dynamic ->
            val `object` = info.`object`
            if (`object` is Substation) {
                val countryId = `object`.name
                info.html = "<h2>$countryId</h2>"
                info.style = unsafeJso<dynamic> {
                    backgroundColor = "#ffffff09"
                    color = "#333359"
                    fontSize = "0.8em"
                    borderRadius = "6px"
                    padding = "0px 10px 10px 10px"
                }
//            console.log("Click on ${info.`object`}")
                info
            } else if (`object` is Line) {
                val countryId = `object`.longName
                info.html = "<h2>$countryId</h2>"
                info.style = unsafeJso<dynamic> {
                    backgroundColor = "#ffffff09"
                    color = "#333359"
                    fontSize = "0.8em"
                    borderRadius = "6px"
                    padding = "0px 10px 10px 10px"
                }
//                console.log("Click on ${info.`object`}")
                info
            } else if (`object` is RiskComponentPolygon) {
//            val countryId = `object`.risk.lines.flatMap { it.risk_list ?: emptyList() }.distinct().joinToString { it.toString() }
                val countryId = `object`.riskComponent.lines.sumOf { it -> it.assetCount ?: 0 }
                info.html = "<h2>$countryId assets at risk</h2>"
                info.style = unsafeJso<dynamic> {
                    backgroundColor = "#ffffff09"
                    color = "#333359"
                    fontSize = "0.8em"
                    borderRadius = "6px"
                    padding = "0px 10px 10px 10px"
                }
//                console.log("Click on ${info.`object`}")
                info
            } else if (`object` is Lightning) {
                val strokeTime = `object`.strokeTime
                val geometry = `object`.geometry
                console.log("Lightning at $strokeTime in $geometry")
                info.html = "<h2>Lightning at $strokeTime<br>in $geometry</h2>"
                info.style = unsafeJso<dynamic> {
                    backgroundColor = "#ffffffcc"
                    color = "#333359"
                    fontSize = "0.8em"
                    borderRadius = "6px"
                    padding = "0px 10px 10px 10px"
                    display = "inline-block"      // <- allows shrinking to content
                    whiteSpace = "nowrap"         // <- prevents wrapping if you want 1 line
                    minWidth = "unset"            // <- remove default minWidth
                    maxWidth = "300px"            // <- optional cap for long text
                    boxShadow = "0 2px 10px rgba(0,0,0,0.2)" // optional, makes it prettier
                    overflow = "hidden"           // <- hide overflow
                    textOverflow = "ellipsis"     // <- add "..." at the end
                }
                info
            } else if (`object` is LightningCluster) {
                val riskTime = `object`.risk_time
                val cluster_id = `object`.cluster_id
                console.log("Cluster $cluster_id")
                info.html = "<h2>Cluster at ${riskTime}<br>id $cluster_id</h2>"
                info.style = unsafeJso<dynamic> {
                    backgroundColor = "#ffffffcc"
                    color = "#333359"
                    fontSize = "0.8em"
                    borderRadius = "6px"
                    padding = "0px 10px 10px 10px"
                    display = "inline-block"      // <- allows shrinking to content
                    whiteSpace = "nowrap"         // <- prevents wrapping if you want 1 line
                    minWidth = "unset"            // <- remove default minWidth
                    maxWidth = "300px"            // <- optional cap for long text
                    boxShadow = "0 2px 10px rgba(0,0,0,0.2)" // optional, makes it prettier
                    overflow = "hidden"           // <- hide overflow
                    textOverflow = "ellipsis"     // <- add "..." at the end
                }
                info
            } else {
                if (`object` != null) {
                    info.html =
                        if (`object`.properties.risk_list != null) "<h2>${`object`.properties.risk_list}</h2>" else "<h2>no risk</h2>"
                    info.style = unsafeJso<dynamic> {
                        backgroundColor = "#ffffff09"
                        color = "#333359"
                        fontSize = "0.8em"
                        borderRadius = "6px"
                        padding = "0px 10px 10px 10px"
                    }
                    info
                } else {
                    null
                }
            }
        }
    }

    DeckGL {
        initialViewState = INITIAL_VIEW_STATE
        controller = unsafeJso<ControllerProps> {
            doubleClickZoom = false
        }
        this.layers = (props.preLayers + listOf(lineLayer) + props.postLayers).toTypedArray()
//        ContextProvider={ _MapContext.Provider}
        asDynamic().getTooltip = tooltip

        asDynamic().onViewStateChange = viewStateChangeCallback

        this.asDynamic().onClick = { info: dynamic, event: dynamic ->
            val button = event.srcEvent.button as Int

            when (button) {
                0 -> { // left click
                    if (info.`object` != null) {
                        // Unreachable if layers have onClick handlers
                        console.log("Left-click on object in layer ${info.layer.id}", info.`object`)
                    } else {
                        // Left-click outside all layers
                        props.dataLoader.controller.primaryLogicalBusSelection.clearSelection()
                        props.dataLoader.controller.secondaryLogicalBusSelection.clearSelection()
                        props.dataLoader.controller.primaryLogicalBranchSelection.clearSelection()
                        props.dataLoader.controller.secondaryLogicalBranchSelection.clearSelection()
                        props.dataLoader.controller.primaryCoordinateSelection.clearSelection()
                        props.dataLoader.controller.secondaryCoordinateSelection.clearSelection()
                    }
                }
//                2 -> { // right click
//                    event.srcEvent.preventDefault() // stop context menu
//                    if (info.`object` != null) {
//                        console.log("Right-click on object in layer ${info.layer.id}", info.`object`)
//                    } else {
//                        console.log("Right-click outside all layers")
//                    }
//                }
            }
        }

        ReactMapGL {
//            this.mapStyle = "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.lightbasemap.vt/style.json"
//            this.mapStyle = "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.imagerybasemap.vt/style.json"
//            this.mapStyle = "ch.swisstopo.lightbasemap.vt/style.json"
            this.mapStyle = props.mapStyle
            this.asDynamic().attributionControl = false
            this.onViewportChange = { viewport -> }
            this.ref = mapRef
            this.asDynamic().onLoad = {
//                println("Map loading ${mapRef.current.getMap()}")
//                val r = mapRef.current.getMap().fitBounds(bounds, unsafeJso {padding = 20});
//                println("Map loaded ${r}")
//                console.log(r)
            }
        }

//        NavigationControl {
//            visualizePitch = true
//        }
    }
}